import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type {
  TypeGetDashboardDeliveryStatusParams,
  TypeGetDashboardDeliveryStatusResponse,
} from '~/types/delivery-agency.types';

/**
 * [배송 현황 대시보드] 실시간 배송 현황 조회 API
 */
const fetchDashboardDeliveryStatus = async (
  params: TypeGetDashboardDeliveryStatusParams
): Promise<TypeGetDashboardDeliveryStatusResponse> => {
  const { data } = await axiosInstance.get<TypeGetDashboardDeliveryStatusResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_DASHBOARD_DELIVERY_STATUS,
    { params }
  );
  return data;
};

type UseGetDashboardDeliveryStatusQueryOptions = {
  params: TypeGetDashboardDeliveryStatusParams;
  enabled?: boolean;
};

/**
 * [배송 현황 대시보드] 실시간 배송 현황 조회 Query Hook
 */
export const useGetDashboardDeliveryStatusQuery = ({
  params,
  enabled = true,
}: UseGetDashboardDeliveryStatusQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.dashboardDeliveryStatus(params),
    queryFn: () => fetchDashboardDeliveryStatus(params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
  });
};

registerMockAPI<TypeGetDashboardDeliveryStatusResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/dashboard/delivery-status',
  label: '배송 현황/실시간',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          summary: {
            totalCount: 600,
            nightDeliveryCount: 100,
            dawnDeliveryCount: 300,
            dayDeliveryCount: 200,
            deliveryAccepted: { count: 600, total: 600, rate: 100 },
            deliveryCompleted: { count: 360, total: 600, rate: 60 },
            deliveryCanceled: { count: 12, total: 600, rate: 2 },
            notInNmPickUpOffice: { count: 40, total: 600, rate: 7 },
            deliveryDelayed: { count: 18, total: 600, rate: 3 },
            deliveryFailed: { count: 20, total: 600, rate: 3 },
          },
          nightDelivery: {
            inNmPickUpOffice: { rate: 90, details: [] },
            inDeliveryCenter: { rate: 72, details: [] },
            inCourierPickUpCenter: null,
            deliveryStart: { rate: 54, details: [] },
            deliveryCompleted: { rate: 36, details: [] },
          },
          dawnDelivery: {
            inNmPickUpOffice: { rate: 85, details: [] },
            inDeliveryCenter: { rate: 68, details: [] },
            inCourierPickUpCenter: null,
            deliveryStart: { rate: 51, details: [] },
            deliveryCompleted: { rate: 34, details: [] },
          },
          dayDelivery: {
            inNmPickUpOffice: { rate: 80, details: [] },
            inDeliveryCenter: null,
            inCourierPickUpCenter: { rate: 64, details: [] },
            deliveryStart: { rate: 48, details: [] },
            deliveryCompleted: { rate: 32, details: [] },
          },
        },
      },
    },
    {
      name: '데이터 없음',
      response: {
        data: {
          summary: {
            totalCount: 0,
            nightDeliveryCount: 0,
            dawnDeliveryCount: 0,
            dayDeliveryCount: 0,
            deliveryAccepted: { count: 0, total: 0, rate: 0 },
            deliveryCompleted: { count: 0, total: 0, rate: 0 },
            deliveryCanceled: { count: 0, total: 0, rate: 0 },
            notInNmPickUpOffice: { count: 0, total: 0, rate: 0 },
            deliveryDelayed: { count: 0, total: 0, rate: 0 },
            deliveryFailed: { count: 0, total: 0, rate: 0 },
          },
          nightDelivery: {
            inNmPickUpOffice: { rate: 0, details: [] },
            inDeliveryCenter: { rate: 0, details: [] },
            inCourierPickUpCenter: null,
            deliveryStart: { rate: 0, details: [] },
            deliveryCompleted: { rate: 0, details: [] },
          },
          dawnDelivery: {
            inNmPickUpOffice: { rate: 0, details: [] },
            inDeliveryCenter: { rate: 0, details: [] },
            inCourierPickUpCenter: null,
            deliveryStart: { rate: 0, details: [] },
            deliveryCompleted: { rate: 0, details: [] },
          },
          dayDelivery: {
            inNmPickUpOffice: { rate: 0, details: [] },
            inDeliveryCenter: null,
            inCourierPickUpCenter: { rate: 0, details: [] },
            deliveryStart: { rate: 0, details: [] },
            deliveryCompleted: { rate: 0, details: [] },
          },
        },
      },
    },
  ],
});
